// utils/welcomeWizardPosting.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- SHARED, production POST /payment-methods body
// builder used by WelcomeWizard's finish step (Finding 3). Submits through the
// explicit percent channel (fee_rate_percent, 0..100), never a bare fee_rate and
// never divided by 100. Extracted so the exact page logic is unit-testable.

// FEE-RATE-SEMANTICS-1 Phase 2B (Finding 4): the wizard draft storage key +
// restore read. Bumped v1 -> v2 because pre-2B snapshots stored a PERCENT-valued
// `fee_rate` (old field) and no `fee_rate_percent`; the new writer reads only
// `fee_rate_percent`, so restoring a v1 snapshot would submit that method with a
// silent 0 rate. Because the key changed, a v1 draft is at a DIFFERENT key and
// is simply never read again. WelcomeWizard uses these exact helpers (no
// test-only duplicate).

/** localStorage key for the wizard draft (v2). */
export function wizardStorageKey(companyId?: string | number | null): string {
  return companyId ? `rb_wizard_v2:${companyId}` : "rb_wizard_v2";
}

/**
 * Read + parse the current-version wizard draft snapshot for `companyId`, or
 * null when: nothing stored at the v2 key (so a pre-2B v1 draft is ignored),
 * the stored JSON is unparseable, or it belongs to a different company. This is
 * the EXACT read WelcomeWizard's restore effect performs.
 */
export function readWizardRestoreSnapshot(
  companyId: string | number,
): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(wizardStorageKey(companyId));
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== "object") return null;
    if (s.company_id && String(s.company_id) !== String(companyId)) return null;
    return s as Record<string, unknown>;
  } catch {
    return null;
  }
}

export type WizardMethodInput = {
  name: string;
  type: string;
  // PERCENT value the user typed in "Charge Rate (%)" (never a fraction).
  fee_rate_percent?: number;
  fixed_fee?: number;
  provider?: string;
  provider_method?: string;
  online?: boolean;
};

export type WizardMethodCreateBody = {
  name: string;
  type: string;
  fee_rate_percent: number;
  fixed_fee: number;
  account_id: number;
  provider?: string;
  provider_method?: string;
  online?: boolean;
};

// Methods that never carry a processing fee regardless of any entered value.
const ZERO_FEE_METHOD_NAMES = ["Cash", "On Account", "Other", "Stripe", "Stripe Link"];

/**
 * Build the /payment-methods create body for a wizard payment method. Reads the
 * PERCENT-valued `fee_rate_percent` field and submits it verbatim (clamped to
 * 0..100, <=4dp), never a bare fee_rate and never a /100 fraction.
 */
export function welcomeWizardMethodCreateBody(
  pm: WizardMethodInput,
  accountId: number,
): WizardMethodCreateBody {
  const rawPct = Number(pm.fee_rate_percent ?? 0);
  const pct = Number.isFinite(rawPct) ? rawPct : 0;
  const feeRatePercent = ZERO_FEE_METHOD_NAMES.includes(pm.name)
    ? 0
    : Number(Math.max(0, Math.min(100, pct)).toFixed(4));
  return {
    name: pm.name,
    type: pm.type,
    fee_rate_percent: feeRatePercent,
    fixed_fee: Number(pm.fixed_fee ?? 0),
    account_id: accountId,
    ...(pm.provider ? { provider: pm.provider } : {}),
    ...(pm.provider_method ? { provider_method: pm.provider_method } : {}),
    ...(pm.online !== undefined ? { online: pm.online } : {}),
  };
}
